import { useEffect, useRef, useState } from 'react';

/**
 * The rendered height of a same-origin srcDoc iframe's content.
 *
 * ⚠ THE PREVIEW WAS A LETTERBOX ONTO A 4,600px PAGE. The frame was pinned to
 * `calc(100vh - 13rem)` and then two notice banners took their share of that,
 * so on a 678px viewport roughly 330px of page was visible — about 7% of the
 * draft — and the iframe's own scrollbar was hidden on purpose, leaving no
 * signal that anything more existed. It did not read as "scroll me", it read
 * as "the page is cut" (Jacob, 2026-08-23).
 *
 * Letting the frame take its content's height and the pane scroll normally
 * gives one scrollbar and the whole page, which is how you review a page.
 *
 * ⚠ MEASURE ON EVERY CHANGE, NOT ONCE ON LOAD. The lead form is a third-party
 * iframe that arrives after load and then resizes itself (GoHighLevel grew
 * from 640px to 1,046px), so a single measurement at `load` crops the page
 * just below the form. The ResizeObserver is what makes it hold.
 */
export function useFrameContentHeight(enabled: boolean, srcDoc: string) {
  const ref = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const frame = ref.current;
    // ⚠ NO setState IN THE EFFECT BODY. Clearing the height here to reset it
    // would be a synchronous setState inside an effect — a cascading render,
    // and the lint rule that catches it is right. `enabled` is applied to the
    // RETURNED value instead, so disabling is a derivation rather than a
    // write. It also means a height survives a srcDoc swap until the new
    // measurement lands, which is what stops the frame flashing at 0px.
    if (!enabled || !frame) return;

    let observer: ResizeObserver | null = null;

    const measure = () => {
      const doc = frame.contentDocument;
      const root = doc?.documentElement;
      if (!root) return;
      const measured = Math.max(root.scrollHeight, doc?.body?.scrollHeight ?? 0);
      // ⚠ NEVER SHRINK TO ZERO. A measurement taken while the document is
      // being replaced reads 0, and a 0px frame looks exactly like a failed
      // render — the absence-versus-failure confusion again, this time in
      // pixels.
      if (measured > 0) setHeight(measured);
    };

    const attach = () => {
      measure();
      const body = frame.contentDocument?.body;
      if (body && typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure);
        observer.observe(body);
      }
    };

    frame.addEventListener('load', attach);
    // srcDoc set before this effect ran: the load event already fired.
    if (frame.contentDocument?.readyState === 'complete') attach();

    return () => {
      frame.removeEventListener('load', attach);
      observer?.disconnect();
    };
  }, [enabled, srcDoc]);

  return { ref, height: enabled ? height : 0 };
}
